// src/features/admin/orders/ResultsDetailDrawer.tsx
import React from "react";
import Button from "components/ui/Button";
import type {
  AdminManualResultIn,
  AdminOrderResultDetailOut,
  AdminOrderResultRowOut,
} from "api/adminResults";
import ManualResultsForm from "./ManualResultsForm";

type Mode = "view" | "create" | "edit";

function prettyRole(v: string | null): string {
  if (v === "branded") return "Branded";
  if (v === "original") return "Original";
  return "—";
}

function openUrl(r: AdminOrderResultRowOut): string | null {
  return r.file_url ? String(r.file_url) : null;
}

function isPlainObject(v: any): v is Record<string, any> {
  return !!v && typeof v === "object" && !Array.isArray(v);
}

type MarkerRow = {
  code?: string;
  name?: string;
  value?: any;
  unit?: string;
  ref_low?: any;
  ref_high?: any;
  flag?: any;
  notes?: any;
};

function normalizeMarkers(markers: any): MarkerRow[] {
  if (Array.isArray(markers)) {
    return markers.map((m: any) => ({
      code: typeof m?.code === "string" ? m.code : undefined,
      name: typeof m?.name === "string" ? m.name : undefined,
      value: m?.value ?? m?.raw_value,
      unit: typeof m?.unit === "string" ? m.unit : undefined,
      ref_low: m?.ref_low,
      ref_high: m?.ref_high,
      flag: m?.flag,
      notes: m?.notes ?? (Array.isArray(m?.comments) ? m.comments.join(" ") : undefined),
    }));
  }
  if (isPlainObject(markers)) {
    return Object.entries(markers).map(([code, m]: [string, any]) => ({
      code,
      name: typeof m?.name === "string" ? m.name : code,
      value: m?.value ?? m?.raw_value,
      unit: typeof m?.unit === "string" ? m.unit : undefined,
      ref_low: m?.ref_low,
      ref_high: m?.ref_high,
      flag: m?.flag,
      notes: m?.notes ?? (Array.isArray(m?.comments) ? m.comments.join(" ") : undefined),
    }));
  }
  return [];
}

function normalizeContentJson(cj: any): {
  patient: Record<string, any> | null;
  markers: MarkerRow[];
  notes?: any;
  schema_version?: any;
} {
  const o = isPlainObject(cj) ? cj : {};
  return {
    patient: isPlainObject(o.patient) ? o.patient : null,
    markers: normalizeMarkers(o.markers),
    notes: o.notes,
    schema_version: o.schema_version,
  };
}

function renderKeyValueGrid(obj: Record<string, any>) {
  const entries = Object.entries(obj);
  if (entries.length === 0) return null;

  return (
    <div className="grid sm:grid-cols-2 gap-2">
      {entries.map(([k, v]) => (
        <div key={k} className="text-sm">
          <div className="text-gray-500">{k}</div>
          <div className="break-words">{String(v ?? "—")}</div>
        </div>
      ))}
    </div>
  );
}

interface Props {
  open: boolean;
  mode: Mode;

  row: AdminOrderResultRowOut | null;
  detail: AdminOrderResultDetailOut | null;
  loadingDetail: boolean;

  onClose: () => void;

  onRender: (r: AdminOrderResultRowOut) => void;

  onCreateSubmit: (payload: AdminManualResultIn) => Promise<void>;
  onEditSubmit: (
    payload: AdminManualResultIn,
    ctx: { row: AdminOrderResultRowOut; detail: AdminOrderResultDetailOut | null }
  ) => Promise<void>;
}

const ResultsDetailDrawer: React.FC<Props> = ({
  open,
  mode,
  row,
  detail,
  loadingDetail,
  onClose,
  onRender,
  onCreateSubmit,
  onEditSubmit,
}) => {
  if (!open) return null;

  const title =
    mode === "create" ? "New result" : mode === "edit" ? "Edit result" : "Result details";

  const cj = normalizeContentJson(detail?.content_json);
  const url = row ? openUrl(row) : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      <div className="relative w-full max-w-5xl bg-white shadow-xl rounded-2xl overflow-hidden">
        <div className="flex items-start justify-between gap-3 p-4 border-b">
          <div>
            <div className="text-xl font-semibold">{title}</div>
            {row ? (
              <div className="text-sm text-gray-600 mt-1">
                #{row.id} · {row.status} · {prettyRole(row.file_role_code)}
              </div>
            ) : null}
          </div>

          <Button type="button" variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>

        <div className="p-4 max-h-[80vh] overflow-y-auto space-y-4">
          {mode === "create" ? (
            <ManualResultsForm
              initialData={null}
              initialTitle=""
              initialSummary=""
              submitLabel="Create"
              onSubmit={onCreateSubmit}
              initialGeneratePdf={true}
            />
          ) : mode === "edit" ? (
            <ManualResultsForm
              initialData={detail?.content_json}
              initialTitle={detail?.title ?? row?.title ?? ""}
              initialSummary={detail?.summary ?? row?.summary ?? ""}
              submitLabel="Save"
              onSubmit={(payload) => {
                if (!row) return Promise.resolve();
                return onEditSubmit(payload, { row, detail });
              }}
              initialGeneratePdf={false}
            />
          ) : !row ? (
            <div className="text-gray-600">No result selected.</div>
          ) : loadingDetail ? (
            <div className="text-sm text-gray-600 italic">Loading…</div>
          ) : (
            <div className="space-y-4">
              <div className="border rounded-2xl p-4 space-y-2">
                <div className="grid sm:grid-cols-2 gap-2 text-sm">
                  <div>
                    <div className="text-gray-500">Created</div>
                    <div>{row.created_date ? new Date(row.created_date).toLocaleString() : "—"}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Status</div>
                    <div className="capitalize">{row.status}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Role</div>
                    <div>{prettyRole(row.file_role_code)}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">File</div>
                    <div>{row.file_name || "—"}</div>
                  </div>
                </div>

                <div className="pt-2 flex gap-2 flex-wrap">
                  {url ? (
                    <a
                      href={url}
                      target="_blank"
                      rel="noreferrer"
                      className="py-2 px-4 font-semibold rounded focus:outline-none focus:ring bg-gray-200 hover:bg-gray-300 text-gray-800 inline-block"
                    >
                      Open file
                    </a>
                  ) : null}

                  <Button type="button" variant="secondary" onClick={() => onRender(row)}>
                    Create/Re-create PDF
                  </Button>
                </div>
              </div>

              <div className="border rounded-2xl p-4 space-y-3">
                <div className="font-medium">Structured content</div>

                {!detail?.content_json ? (
                  <div className="text-sm text-gray-600">No content_json on this result.</div>
                ) : (
                  <>
                    {cj.patient ? (
                      <div className="text-sm">
                        <div className="text-gray-500 mb-2">Patient</div>
                        {renderKeyValueGrid(cj.patient)}
                      </div>
                    ) : null}

                    <div className="text-sm">
                      <div className="text-gray-500 mb-2">Markers</div>
                      {cj.markers.length === 0 ? (
                        <div className="text-sm text-gray-600">No markers found.</div>
                      ) : (
                        <div className="overflow-x-auto border rounded-xl">
                          <table className="min-w-full text-sm">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="text-left p-2">Code</th>
                                <th className="text-left p-2">Name</th>
                                <th className="text-left p-2">Value</th>
                                <th className="text-left p-2">Unit</th>
                                <th className="text-left p-2">Ref low</th>
                                <th className="text-left p-2">Ref high</th>
                                <th className="text-left p-2">Notes</th>
                              </tr>
                            </thead>
                            <tbody>
                              {cj.markers.map((m, idx) => (
                                <tr key={idx} className="border-t">
                                  <td className="p-2">{m.code ?? "—"}</td>
                                  <td className="p-2">{m.name ?? "—"}</td>
                                  <td className="p-2">{String(m.value ?? "—")}</td>
                                  <td className="p-2">{m.unit ?? "—"}</td>
                                  <td className="p-2">{String(m.ref_low ?? "—")}</td>
                                  <td className="p-2">{String(m.ref_high ?? "—")}</td>
                                  <td className="p-2">{String(m.notes ?? "—")}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>

                    {cj.notes ? (
                      <div className="text-sm">
                        <div className="text-gray-500">Notes</div>
                        <div className="whitespace-pre-wrap">{String(cj.notes)}</div>
                      </div>
                    ) : null}

                    {cj.schema_version ? (
                      <div className="text-xs text-gray-500">
                        schema_version: {String(cj.schema_version)}
                      </div>
                    ) : null}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResultsDetailDrawer;
